<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useCatalogStore } from '@/stores/catalog';
import { api } from '@/composables/useApi';
import { ROLE_LABELS } from '@/lib/refereeRoles';
import { reasonLabel } from '@/lib/cancelReasons';
import { levelPillClass } from '@/lib/levels';
import { UserSearch, Calendar, Clock, MapPin, X, ArrowLeft } from 'lucide-vue-next';
import IconTile from '@/components/IconTile.vue';
import RefereeCard from '@/components/RefereeCard.vue';

/**
 * RefereePreviewModal — admin-side "see what a referee sees" tool.
 *
 * Flow:
 *   1. Admin types/picks a referee from the catalog (autocomplete on
 *      name + license_id).
 *   2. Modal switches to a preview frame that loads that referee's
 *      nominations and renders a stripped-down version of the
 *      RefereeDashboardView — the same upcoming / past-due / complete
 *      sections, the same date range filter, but read-only.
 *
 * Strictly client-side: it queries the same `/api/nominations?referee_id=…`
 * endpoint the admin already has access to. No impersonation, no
 * session swap.
 */

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const catalog = useCatalogStore();
const search = ref('');
const selected = ref<any | null>(null);

const todayDate = new Date();
const today = todayDate.toISOString().slice(0, 10);
const from = ref(new Date(todayDate.getFullYear(), todayDate.getMonth() - 3, 1).toISOString().slice(0, 10));
const to   = ref(new Date(todayDate.getFullYear(), todayDate.getMonth() + 3, 0).toISOString().slice(0, 10));

const loading = ref(false);
const error = ref<string | null>(null);
const rows = ref<any[]>([]);

onMounted(async () => {
    if (!catalog.referees.length) await catalog.fetchKey('referees');
});

watch(() => props.open, (v) => {
    if (v) {
        // Reset on open so each preview session starts clean.
        search.value = '';
        selected.value = null;
        error.value = null;
        rows.value = [];
    }
});

function refName(r: any): string {
    return [r.first_name, r.last_name].filter(Boolean).join(' ') || r.id;
}

// Alphabetical by the displayed name, case-insensitive — applies
// whether the admin is scrolling the full roster or scanning a
// filtered subset.
const refereesSorted = computed<any[]>(() =>
    [...(catalog.referees as any[])]
        .sort((a, b) => refName(a).localeCompare(refName(b), undefined, { sensitivity: 'base' }))
);
const refereeList = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (!term) return refereesSorted.value.slice(0, 30);
    return refereesSorted.value.filter(r => {
        const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
        const lic = (r.license_id || '').toLowerCase();
        return name.includes(term) || lic.includes(term);
    }).slice(0, 30);
});

async function pick(r: any) {
    selected.value = r;
    await loadPreview();
}

async function loadPreview() {
    if (!selected.value) return;
    loading.value = true;
    error.value = null;
    try {
        const q = new URLSearchParams({ referee_id: selected.value.id });
        if (from.value) q.set('from', from.value);
        if (to.value)   q.set('to',   to.value);
        rows.value = await api<any[]>('/nominations?' + q.toString());
    } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
    } finally {
        loading.value = false;
    }
}

watch([from, to], () => { if (selected.value) loadPreview(); });

function teamLine(r: any): string {
    return `${r.team_a_name || '—'} vs ${r.team_b_name || '—'}`;
}
function venueLine(r: any): string {
    return [r.match_city, r.match_hall].filter(Boolean).join(' — ');
}

const upcoming = computed(() => rows.value.filter(r =>
    !['completed', 'declined', 'cancelled'].includes(r.status) && (!r.match_date || r.match_date >= today)));
// Everything that already happened or is terminal — one "Past matches" list.
const past = computed(() => rows.value.filter(r =>
    ['completed', 'declined', 'cancelled'].includes(r.status) || (!!r.match_date && r.match_date < today)));

function close() { emit('close'); }
function back() { selected.value = null; rows.value = []; }

// Profile preview popup (passport card) with photo update.
const showProfile = ref(false);
async function onPhotoChanged() {
    await catalog.fetchKey('referees');
    if (selected.value) {
        const fresh = (catalog.referees as any[]).find(r => r.id === selected.value.id);
        if (fresh) selected.value = fresh;
    }
}

// Lock body scroll while open, native dialog handling done in template.
watch(() => props.open, (v) => {
    document.body.style.overflow = v ? 'hidden' : '';
});
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="rpm-backdrop" @click.self="close">
            <div class="rpm-shell" role="dialog" aria-modal="true" aria-label="Preview as referee">
                <!-- Decorative orbs anchor the teal aesthetic. -->
                <div class="rpm-orb rpm-orb-a" aria-hidden="true"></div>
                <div class="rpm-orb rpm-orb-b" aria-hidden="true"></div>

                <header class="rpm-head">
                    <div class="rpm-head-left">
                        <IconTile :icon="UserSearch" size="md" halo />
                        <div>
                            <p class="rpm-eyebrow">ARAVO · Admin tools</p>
                            <h2 class="rpm-title">
                                <template v-if="!selected">Preview as referee</template>
                                <template v-else>{{ refName(selected) }} — referee preview</template>
                            </h2>
                        </div>
                    </div>
                    <button @click="close" class="rpm-close" aria-label="Close preview">
                        <X :size="18" />
                    </button>
                </header>

                <!-- STEP 1: pick referee -->
                <div v-if="!selected" class="rpm-body">
                    <label class="rpm-search-label">
                        <span>Referee name or licence</span>
                        <input v-model="search" type="text" autofocus
                               placeholder="Start typing a name…"
                               class="rpm-search-input" />
                    </label>

                    <ul v-if="refereeList.length" class="rpm-ref-list">
                        <li v-for="r in refereeList" :key="r.id">
                            <button @click="pick(r)" class="rpm-ref-row">
                                <span class="rpm-ref-avatar">{{ (r.first_name || '?').charAt(0).toUpperCase() }}</span>
                                <span class="rpm-ref-meta">
                                    <strong>{{ refName(r) }}</strong>
                                    <span class="rpm-ref-sub">
                                        <template v-if="r.license_id">Licence {{ r.license_id }}</template>
                                        <span v-if="r.level_rank || r.grade" class="rpm-level"
                                              :class="levelPillClass(r.level_rank || r.grade)">
                                            {{ r.level_rank || r.grade }}
                                        </span>
                                    </span>
                                </span>
                                <span class="rpm-ref-go">Preview →</span>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="rpm-empty">No referees match that search.</p>
                </div>

                <!-- STEP 2: preview the referee's dashboard -->
                <div v-else class="rpm-body">
                    <div class="rpm-preview-controls">
                        <button @click="back" class="rpm-back-btn">
                            <ArrowLeft :size="14" /> Pick a different referee
                        </button>
                        <button @click="showProfile = true" class="rpm-back-btn rpm-profile-btn">
                            Preview profile
                        </button>
                        <div class="rpm-range">
                            <label>
                                <span>From</span>
                                <input v-model="from" type="date" />
                            </label>
                            <label>
                                <span>To</span>
                                <input v-model="to" type="date" />
                            </label>
                        </div>
                    </div>

                    <div v-if="error" class="rpm-error">{{ error }}</div>
                    <div v-if="loading" class="rpm-state">Loading the referee's view…</div>

                    <div v-else class="rpm-mirror">
                        <!-- Mirror of the referee's hero strip -->
                        <section class="rpm-hero-mirror">
                            <p class="rpm-eyebrow">Referee workspace · what they see</p>
                            <h3>Welcome, {{ refName(selected) }}.</h3>
                            <p class="rpm-mirror-sub">
                                {{ rows.length }} designation{{ rows.length === 1 ? '' : 's' }} in this period.
                            </p>
                        </section>

                        <!-- Upcoming (green) -->
                        <section class="rpm-sect rpm-sect--green">
                            <div class="rpm-sect-head">
                                <span>Upcoming</span>
                                <span class="rpm-sect-count">{{ upcoming.length }}</span>
                            </div>
                            <p v-if="!upcoming.length" class="rpm-state">Nothing upcoming.</p>
                            <ul v-else class="rpm-row-list">
                                <li v-for="r in upcoming" :key="r.id">
                                    <span class="rpm-row-date"><Calendar :size="12" />{{ r.match_date || '—' }}</span>
                                    <span class="rpm-row-time"><Clock :size="12" />{{ r.match_time || '—' }}</span>
                                    <span class="rpm-row-match">{{ teamLine(r) }}</span>
                                    <span class="rpm-row-venue"><MapPin :size="12" />{{ venueLine(r) || '—' }}</span>
                                    <span class="rpm-row-role">{{ ROLE_LABELS[r.role] || r.role }}</span>
                                </li>
                            </ul>
                        </section>

                        <!-- Past matches (blue) -->
                        <section class="rpm-sect rpm-sect--blue">
                            <div class="rpm-sect-head">
                                <span>Past matches</span>
                                <span class="rpm-sect-count">{{ past.length }}</span>
                            </div>
                            <p v-if="!past.length" class="rpm-state">No past matches in range.</p>
                            <ul v-else class="rpm-row-list">
                                <li v-for="r in past" :key="r.id" :class="r.status === 'cancelled' ? 'is-cancelled' : ''">
                                    <span class="rpm-row-date"><Calendar :size="12" />{{ r.match_date || '—' }}</span>
                                    <span class="rpm-row-time"><Clock :size="12" />{{ r.match_time || '—' }}</span>
                                    <span class="rpm-row-match">
                                        {{ teamLine(r) }}
                                        <em v-if="r.status === 'cancelled'" class="rpm-row-note">
                                            Cancelled — {{ reasonLabel((typeof r.match_doc === 'string' ? JSON.parse(r.match_doc) : r.match_doc)?.cancel_reason) }}
                                        </em>
                                    </span>
                                    <span class="rpm-row-venue"><MapPin :size="12" />{{ venueLine(r) || '—' }}</span>
                                    <span class="rpm-row-role">{{ ROLE_LABELS[r.role] || r.role }}</span>
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>

    <!-- Profile popup — passport card with photo update. -->
    <Teleport to="body">
        <div v-if="showProfile && selected" class="rpm-profile-overlay" @click.self="showProfile = false">
            <div class="rpm-profile-shell">
                <button @click="showProfile = false" class="rpm-profile-close" aria-label="Close profile">
                    <X :size="18" />
                </button>
                <RefereeCard :referee="selected" :editable="true" @photo-changed="onPhotoChanged" />
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.rpm-backdrop {
    position: fixed; inset: 0;
    background: rgba(4, 47, 46, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 70;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 2rem 1rem;
    overflow-y: auto;
}

/* "Preview profile" button + popup overlay (above the preview modal). */
.rpm-profile-btn {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    border-color: rgba(45, 212, 191, 0.6);
}
.rpm-profile-overlay {
    position: fixed; inset: 0; z-index: 80;
    background: rgba(2, 44, 34, 0.62);
    backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 2rem 1rem; overflow-y: auto;
}
.rpm-profile-shell { position: relative; width: 100%; max-width: 600px; }
.rpm-profile-close {
    position: absolute; top: -0.6rem; right: -0.2rem; z-index: 1;
    width: 2.2rem; height: 2.2rem; border-radius: 9999px;
    background: #ffffff; border: 1px solid rgba(15, 23, 42, 0.15);
    color: #0f172a; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 8px 18px -8px rgba(0, 0, 0, 0.5);
}
.rpm-shell {
    position: relative;
    width: 100%;
    max-width: 56rem;
    border-radius: 1.4rem;
    overflow: hidden;
    color: #f0fdfa;
    background:
        radial-gradient(circle at 20% 12%, rgba(94, 234, 212, 0.30), transparent 55%),
        radial-gradient(circle at 80% 88%, rgba(5, 150, 105, 0.22), transparent 60%),
        linear-gradient(160deg, #134e4a 0%, #0f766e 38%, #0d9488 70%, #2dd4bf 100%);
    border: 1px solid rgba(94, 234, 212, 0.25);
    box-shadow: 0 30px 60px -22px rgba(0, 0, 0, 0.55);
}
.rpm-orb { position: absolute; border-radius: 50%; filter: blur(60px); pointer-events: none; }
.rpm-orb-a { width: 260px; height: 260px; top: -80px; left: -80px; background: rgba(94, 234, 212, 0.35); }
.rpm-orb-b { width: 320px; height: 320px; bottom: -120px; right: -100px; background: rgba(20, 184, 166, 0.30); }

.rpm-head {
    position: relative; z-index: 1;
    display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
    padding: 1.25rem 1.4rem 0.75rem;
}
.rpm-head-left { display: flex; align-items: center; gap: 0.85rem; }
.rpm-eyebrow {
    margin: 0; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.14em;
    text-transform: uppercase; color: rgba(204, 251, 241, 0.75);
}
.rpm-title { margin: 0.1rem 0 0.25rem; font-size: 1.25rem; font-weight: 800; color: #ffffff; }
.rpm-close {
    width: 2.2rem; height: 2.2rem; border-radius: 9999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #f0fdfa; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background 120ms;
}
.rpm-close:hover { background: rgba(255, 255, 255, 0.22); }

.rpm-body { position: relative; z-index: 1; padding: 1rem 1.4rem 1.4rem; }

/* ─── Step 1: search + list ─── */
.rpm-search-label {
    display: block;
    font-size: 0.62rem; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: rgba(204, 251, 241, 0.75);
    margin-bottom: 0.85rem;
}
.rpm-search-label span { display: block; margin-bottom: 0.3rem; }
.rpm-search-input {
    width: 100%;
    background: rgba(13, 36, 38, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: #f0fdfa; border-radius: 0.6rem;
    padding: 0.7rem 0.85rem; font-size: 0.95rem;
    transition: border-color 120ms, background 120ms, box-shadow 120ms;
}
.rpm-search-input::placeholder { color: rgba(204, 251, 241, 0.4); }
.rpm-search-input:focus {
    outline: none; border-color: rgba(94, 234, 212, 0.65);
    background: rgba(13, 36, 38, 0.88);
    box-shadow: 0 0 0 3px rgba(45, 212, 191, 0.2);
}

.rpm-ref-list {
    list-style: none; padding: 0; margin: 0;
    display: grid; gap: 0.5rem;
    max-height: 24rem; overflow-y: auto;
}
.rpm-ref-row {
    display: flex; align-items: center; gap: 0.85rem; width: 100%;
    padding: 0.7rem 0.85rem;
    background: rgba(15, 35, 38, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    border-radius: 0.75rem;
    color: #f0fdfa; cursor: pointer; text-align: left;
    transition: background 120ms, border-color 120ms, transform 120ms;
}
.rpm-ref-row:hover { background: rgba(15, 35, 38, 0.75); border-color: rgba(94, 234, 212, 0.45); transform: translateY(-1px); }
.rpm-ref-avatar {
    width: 2.2rem; height: 2.2rem; border-radius: 9999px; flex-shrink: 0;
    background: linear-gradient(135deg, #14b8a6, #0d9488);
    display: inline-flex; align-items: center; justify-content: center;
    color: #ffffff; font-weight: 800; font-size: 0.9rem;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
}
.rpm-ref-meta { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.rpm-ref-meta strong { font-size: 0.92rem; color: #ffffff; }
.rpm-ref-sub { font-size: 0.72rem; color: rgba(204, 251, 241, 0.7); }
.rpm-ref-go {
    font-size: 0.72rem; font-weight: 700; color: #ccfbf1;
    letter-spacing: 0.04em; flex-shrink: 0;
}
.rpm-empty { color: rgba(204, 251, 241, 0.7); font-size: 0.85rem; text-align: center; padding: 1.5rem; }

/* ─── Step 2: preview mirror ─── */
.rpm-preview-controls {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 0.75rem; margin-bottom: 0.85rem;
}
.rpm-back-btn {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.4rem 0.85rem; border-radius: 9999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #f0fdfa; font-size: 0.74rem; font-weight: 700;
    cursor: pointer; transition: background 120ms;
}
.rpm-back-btn:hover { background: rgba(255, 255, 255, 0.22); }

.rpm-range { display: flex; gap: 0.5rem; }
.rpm-range label {
    display: inline-flex; flex-direction: column;
    font-size: 0.6rem; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: rgba(204, 251, 241, 0.78);
}
.rpm-range label span { margin-bottom: 0.2rem; }
.rpm-range input {
    background: rgba(13, 36, 38, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #f0fdfa; border-radius: 0.45rem;
    padding: 0.4rem 0.55rem; font-size: 0.78rem;
    color-scheme: dark;
}

.rpm-error {
    margin-bottom: 0.85rem; padding: 0.6rem 0.85rem;
    color: #fecaca; background: rgba(244, 63, 94, 0.18);
    border: 1px solid rgba(244, 63, 94, 0.4); border-radius: 0.55rem;
    font-size: 0.8rem;
}
.rpm-state {
    color: rgba(204, 251, 241, 0.65); font-size: 0.8rem;
    text-align: center; padding: 0.85rem 0;
}
.rpm-mirror { display: flex; flex-direction: column; gap: 0.9rem; }

.rpm-hero-mirror {
    background: rgba(15, 35, 38, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
    border-radius: 0.85rem; padding: 1rem 1.1rem;
}
.rpm-hero-mirror h3 {
    margin: 0.2rem 0 0.25rem; font-size: 1.1rem; font-weight: 800; color: #ffffff;
}
.rpm-mirror-sub { margin: 0; color: rgba(240, 253, 250, 0.78); font-size: 0.78rem; }

.rpm-sect {
    background: rgba(15, 35, 38, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.10);
    backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
    border-radius: 0.85rem; padding: 0.85rem 1rem;
}
.rpm-sect-head {
    display: flex; align-items: baseline; justify-content: space-between;
    margin-bottom: 0.5rem; color: #ffffff;
    font-size: 0.82rem; font-weight: 800; letter-spacing: 0.02em;
}
.rpm-sect-count {
    font-size: 0.7rem; font-weight: 700; color: #ccfbf1;
    background: rgba(20, 184, 166, 0.25);
    padding: 0.1rem 0.55rem; border-radius: 9999px;
}

/* Level chip on the referee list (Beginner grey · Regional green ·
   National blue · International gold — same scheme used app-wide). */
.rpm-level {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.02rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.64rem;
    font-weight: 800;
    letter-spacing: 0.02em;
}

/* Section tones — green for upcoming, blue for past (the dashboard palette). */
.rpm-sect--green { border-left: 3px solid #34d399; }
.rpm-sect--green .rpm-sect-count { background: rgba(16, 185, 129, 0.32); color: #d1fae5; }
.rpm-sect--blue  { border-left: 3px solid #60a5fa; }
.rpm-sect--blue  .rpm-sect-count { background: rgba(59, 130, 246, 0.32); color: #dbeafe; }

.rpm-row-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.4rem; }
.rpm-row-list li {
    display: grid;
    grid-template-columns: minmax(90px, auto) minmax(60px, auto) 1fr minmax(0, 1.2fr) auto;
    align-items: center; gap: 0.65rem;
    padding: 0.55rem 0.65rem;
    background: rgba(13, 36, 38, 0.45);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 0.55rem;
    font-size: 0.78rem;
    color: #ecfeff;
}
.rpm-row-list li.is-warn { background: rgba(245, 158, 11, 0.16); border-color: rgba(245, 158, 11, 0.35); }
.rpm-row-list li.is-cancelled { background: rgba(244, 63, 94, 0.14); border-color: rgba(244, 63, 94, 0.32); }
.rpm-row-list li > span { display: inline-flex; align-items: center; gap: 0.25rem; white-space: nowrap; }
.rpm-row-match { white-space: normal; font-weight: 600; }
.rpm-row-venue { color: rgba(204, 251, 241, 0.75); }
.rpm-row-role {
    padding: 0.15rem 0.55rem; border-radius: 9999px;
    background: rgba(20, 184, 166, 0.3); font-weight: 700; color: #ffffff;
    font-size: 0.7rem;
}
.rpm-row-note {
    display: block; margin-top: 0.15rem; font-style: italic;
    color: #fda4af; font-size: 0.7rem;
}

@media (max-width: 720px) {
    .rpm-row-list li {
        grid-template-columns: auto auto;
        gap: 0.35rem 0.65rem;
    }
    .rpm-row-list li .rpm-row-match,
    .rpm-row-list li .rpm-row-venue { grid-column: 1 / -1; }
}
</style>
